export interface GenerationReference {
	/** Element tag of the plucked root, e.g. "button" or "section". */
	rootTag: string;
	sourceDomain: string;
	sourceTitle?: string | null;
	/** Short text content of the element, already trimmed. */
	textSnippet?: string;
}

/** An earlier Studio generation used as a reference for the next one. */
export interface GeneratedImageReference {
	/** The brief the earlier image was generated from. */
	generatedFromBrief: string;
}

/** An image the user uploaded. */
export interface UploadedImageReference {
	uploadedFileName: string | null;
}

export type PromptReference =
	| GenerationReference
	| GeneratedImageReference
	| UploadedImageReference;

export interface ComposeGenerationPromptParams {
	/** Instructions for the "custom" template. */
	customInstructions?: string | null;
	references: PromptReference[];
	/** The first reference is an earlier design to revise, not inspiration. */
	revisesFirstReference?: boolean;
	/** How the request is framed; see PROMPT_TEMPLATES. Default "generic". */
	template?: PromptTemplateId;
	userPrompt: string;
}

/**
 * Output shape a template asks hosted models for: "auto" follows the input
 * image (edit models) or the model's default (text-to-image).
 */
export type OutputShape = "auto" | "landscape" | "portrait";

export type PromptTemplateId = "generic" | "web" | "app" | "graphic" | "custom";

export interface PromptTemplate {
	description: string;
	id: PromptTemplateId;
	label: string;
	shape: OutputShape;
}

/**
 * How Pluck frames a prompt for the image model. "generic" adds nothing but
 * the references; the design templates add a brief, inspiration-style
 * reference handling and output requirements (the original behaviour).
 */
export const PROMPT_TEMPLATES: PromptTemplate[] = [
	{
		id: "generic",
		label: "Generic",
		description:
			"Sends your prompt as written. Connected images are kept faithful: same subject, composition, style and format.",
		shape: "auto",
	},
	{
		id: "web",
		label: "Website",
		description:
			"Frames every prompt as a polished web page or UI section (16:9), using references as inspiration.",
		shape: "landscape",
	},
	{
		id: "app",
		label: "Mobile app",
		description:
			"Frames every prompt as a mobile app screen (9:16), using references as inspiration.",
		shape: "portrait",
	},
	{
		id: "graphic",
		label: "Graphic design",
		description:
			"Frames every prompt as a poster, social post, ad or banner, using references as inspiration.",
		shape: "auto",
	},
	{
		id: "custom",
		label: "Custom",
		description:
			"Your own instructions, sent before your prompt and the references.",
		shape: "auto",
	},
];

export const DEFAULT_PROMPT_TEMPLATE: PromptTemplateId = "generic";

export function isPromptTemplateId(value: unknown): value is PromptTemplateId {
	return PROMPT_TEMPLATES.some((t) => t.id === value);
}

/** The output shape for a stored template id (older runs: the web shape). */
export function templateShape(id: string | null | undefined): OutputShape {
	return PROMPT_TEMPLATES.find((t) => t.id === (id ?? "web"))?.shape ?? "auto";
}

/** Framing of the design templates (web is the original prompt). */
interface DesignFraming {
	/** Default brief when combining references. */
	combine: string;
	noun: string;
	output: string;
	/** What to make, e.g. "web design image (a full page …)". */
	subject: string;
}

const UI_OUTPUT =
	"Output requirements: one high-resolution image, realistic UI rendering, real-looking copy (no lorem ipsum), no captions, labels, watermarks or annotations outside the design itself.";

const DESIGN_FRAMINGS: Record<"web" | "app" | "graphic", DesignFraming> = {
	web: {
		subject:
			"web design image (a full page or a complete UI section) as a PNG.",
		combine: "Combine the references into one cohesive web design.",
		noun: "design",
		output: UI_OUTPUT,
	},
	app: {
		subject: "mobile app screen design as a PNG.",
		combine: "Combine the references into one cohesive app screen.",
		noun: "design",
		output: UI_OUTPUT,
	},
	graphic: {
		subject:
			"graphic design (such as a poster, social post, ad or banner) as a PNG.",
		combine: "Combine the references into one cohesive graphic design.",
		noun: "design",
		output:
			"Output requirements: one high-resolution image, no captions, labels, watermarks or annotations outside the design itself.",
	},
};

const MAX_SNIPPET_CHARS = 80;
const BRIEF_HEADING = "Design brief:\n";
const WHITESPACE_RE = /\s+/g;

function clip(text: string): string {
	return text.length > MAX_SNIPPET_CHARS
		? `${text.slice(0, MAX_SNIPPET_CHARS - 1)}…`
		: text;
}

/** The user's brief inside a prompt built by `composeGenerationPrompt`. */
export function extractBrief(composedPrompt: string): string {
	const start = composedPrompt.indexOf(BRIEF_HEADING);
	if (start === -1) {
		return composedPrompt.trim();
	}
	const rest = composedPrompt.slice(start + BRIEF_HEADING.length);
	const end = rest.indexOf("\n\n");
	return (end === -1 ? rest : rest.slice(0, end)).trim();
}

export function referenceLabel(ref: PromptReference): string {
	if ("uploadedFileName" in ref) {
		const name = ref.uploadedFileName?.trim();
		return name ? `Uploaded image "${clip(name)}"` : "Uploaded image";
	}
	if ("generatedFromBrief" in ref) {
		const brief = ref.generatedFromBrief.trim().replace(WHITESPACE_RE, " ");
		return brief
			? `Earlier generated design — "${clip(brief)}"`
			: "Earlier generated design";
	}
	const parts = [ref.sourceDomain];
	const title = ref.sourceTitle?.trim();
	if (title) {
		parts.push(`"${title}"`);
	}
	let label = `${parts.join(" ")} — <${ref.rootTag}>`;
	const snippet = ref.textSnippet?.trim();
	if (snippet) {
		label += ` "${clip(snippet)}"`;
	}
	return label;
}

function designDefaultBrief(
	framing: DesignFraming,
	revisesFirstReference: boolean
): string {
	return revisesFirstReference
		? "Refine the first attached design into a more polished version."
		: framing.combine;
}

function designReferencesIntro(
	count: number,
	revisesFirstReference: boolean
): string {
	const attached = `${count} reference image${count === 1 ? " is" : "s are"} attached, in this order.`;
	if (revisesFirstReference) {
		const others =
			count > 1
				? " Use the other images as inspiration for styling and components."
				: "";
		return `${attached} Image 1 is an earlier design to revise: keep its overall layout and content, and change it as the brief asks.${others}`;
	}
	return `${attached} Treat them as inspiration for layout, color, typography and component styling — recombine them into one coherent design rather than copying any of them verbatim:`;
}

function referenceList(references: PromptReference[]): string[] {
	return references.map((ref, index) => `${index + 1}. ${referenceLabel(ref)}`);
}

function composeDesignPrompt(
	framing: DesignFraming,
	{
		userPrompt,
		references,
		revisesFirstReference = false,
	}: ComposeGenerationPromptParams
): string {
	const brief = userPrompt.trim();
	const lines: string[] = [
		`Create a single, polished ${framing.subject}`,
		"",
		"Design brief:",
		brief.length > 0
			? brief
			: designDefaultBrief(framing, revisesFirstReference),
	];
	if (references.length > 0) {
		lines.push(
			"",
			designReferencesIntro(references.length, revisesFirstReference)
		);
		lines.push(...referenceList(references));
	}
	lines.push("", framing.output);
	return lines.join("\n");
}

/** Generic: the prompt as written; references are the material to work on. */
function genericReferencesIntro(
	count: number,
	revisesFirstReference: boolean
): string {
	const attached = `${count} image${count === 1 ? " is" : "s are"} attached, in this order.`;
	if (revisesFirstReference || count === 1) {
		const others =
			count > 1 ? " Use the other images only as the request describes." : "";
		return `${attached} Image 1 is the image to change: keep its subject, composition, framing, style and format, and change only what the request asks.${others}`;
	}
	return `${attached} Work from them as the request describes, staying faithful to their content and style unless asked otherwise:`;
}

function genericDefaultBrief(
	count: number,
	revisesFirstReference: boolean
): string {
	if (revisesFirstReference || count === 1) {
		return "Improve image 1 while keeping it recognisably the same image.";
	}
	return "Combine the attached images into one image.";
}

function composeGenericPrompt(
	{
		userPrompt,
		references,
		revisesFirstReference = false,
	}: ComposeGenerationPromptParams,
	instructions?: string
): string {
	const brief = userPrompt.trim();
	const lines: string[] = [];
	if (instructions) {
		lines.push(instructions, "");
	}
	lines.push(
		brief.length > 0
			? brief
			: genericDefaultBrief(references.length, revisesFirstReference)
	);
	if (references.length > 0) {
		lines.push(
			"",
			genericReferencesIntro(references.length, revisesFirstReference),
			...referenceList(references)
		);
	}
	return lines.join("\n");
}

/**
 * Build the instruction sent to the image model. The reference images are
 * attached separately, in the same order as the numbered list.
 */
export function composeGenerationPrompt(
	params: ComposeGenerationPromptParams
): string {
	const template = params.template ?? DEFAULT_PROMPT_TEMPLATE;
	switch (template) {
		case "web":
		case "app":
		case "graphic":
			return composeDesignPrompt(DESIGN_FRAMINGS[template], params);
		case "custom":
			return composeGenericPrompt(
				params,
				params.customInstructions?.trim() || undefined
			);
		default:
			return composeGenericPrompt(params);
	}
}
